import { defineStore } from 'pinia';

import templatesRepository from '@/api/templatesRepository';
import type {
  GetTemplatesParams,
  SaveTemplatesModalInfo,
  TemplateAttachments,
  TemplatesRow
} from '@/types/template';
import { errorHandler } from '@/utils/utils';

interface State {
  templates: TemplatesRow[] | [];
  allTemplates: TemplatesRow[] | [];
  loading: boolean
  saveTemplateLoading: boolean
  offset: number
  maxResults: number
  pageToken: string | null
  searchPhrase: string
  name: string | number | null
  subject: string | number | null
  bodyHtml: string | null
}

export const useTemplateStore = defineStore('templateStore', {
  state: (): State => {
    return {
      templates: [],
      allTemplates: [],
      loading: true,
      saveTemplateLoading: false,
      offset: 0,
      maxResults: 25,
      pageToken: null,
      searchPhrase: '',
      name: null,
      subject: null,
      bodyHtml: null
    };
  },
  getters: {
    prepareParams(): GetTemplatesParams {
      return {
        search_phrase: this.searchPhrase,
        page_token: this.pageToken,
        max_results: this.maxResults,
        sort_order: 'desc'
      };
    }
  },
  actions: {
    clearState() {
      this.$state.offset = 0;
      this.$state.pageToken = null;
      this.$state.templates = [];
    },

    setOffset() {
      this.$state.offset = this.$state.templates.length;
    },

    prepareTemplateAttachments(attachments: TemplateAttachments) {
      return attachments.map((attachment) => {
        const { uuid, _destroy } = attachment || {};

        if (uuid) {
          return _destroy ? { uuid, _destroy } : { uuid };
        }

        return attachment;
      });
    },

    async getTemplatesList() {
      if (!this.$state.pageToken && this.$state.offset > 0) return;

      const params = this.prepareParams;

      try {
        this.$state.loading = true;

        const { data, metadata } = await templatesRepository.getTemplatesList(params);
        this.$state.pageToken = metadata.nextPageToken;

        this.$state.templates = [...this.$state.templates, ...data];
      } catch(error) {
        errorHandler(error);
      } finally {
        this.setOffset();
        this.$state.loading = false;
      }
    },

    async getAllTemplates() {
      try {
        const params = {
          offset: 0,
          max_results: 1000,
          sort_order: 'desc'
        };

        const { data } = await templatesRepository.getTemplatesList(params);

        this.$state.allTemplates = [...data];
      } catch(error) {
        errorHandler(error);
      }
    },

    async saveTemplate(modalInfo: SaveTemplatesModalInfo) {
      try {
        this.$state.saveTemplateLoading = true;
        const params = {
          name: this.$state.name,
          subject: this.$state.subject,
          body_html: this.$state.bodyHtml,
          attachments: this.prepareTemplateAttachments(modalInfo.attachments)
        };

        await templatesRepository.saveTemplate(params, modalInfo);
      } catch(error) {
        errorHandler(error);
      } finally {
        this.$state.saveTemplateLoading = false;
        this.clearState();
        await this.getTemplatesList();
      }
    },

    async getTemplateInfo(uuid?: string) {
      try {
        if (!uuid) return;
        const { data } = await templatesRepository.getTemplateInfo(uuid);

        return data;
      } catch(error) {
        errorHandler(error);
      }
    }
  }
});
